// Doc editor tag-chip input.
//
// Activates when `#js-tag-typer` exists. Enter or comma adds the tag;
// clicking the `.js-tag-closer` removes it. Talks to /ca/docs/api/setTag.php
// and /ca/docs/api/deleteTag.php directly — DocumentManager owns the logic.

interface SetTagResponse {
    id: number;
    sent?: { tag: string };
}

function makeTagChip(id: number | string, label: string): HTMLSpanElement {
    const span = document.createElement('span');
    span.className = 'badge text-bg-primary';
    span.setAttribute('data-tid', String(id));
    span.innerHTML = `<span class="js-tag-closer">&times;</span>${escapeHtml(label)}`;
    return span;
}

function escapeHtml(s: string): string {
    const d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
}

async function postForm<T>(url: string, data: Record<string, string>): Promise<T> {
    const body = new URLSearchParams(data);
    const res = await fetch(url, { method: 'POST', body });
    const text = await res.text();
    try {
        return JSON.parse(text) as T;
    } catch {
        return text as unknown as T;
    }
}

export async function addTag(tag: string, docId: string): Promise<SetTagResponse | null> {
    if (!tag || !docId) {
        return null;
    }
    return postForm<SetTagResponse>('/ca/docs/api/setTag.php', { tag, docid: docId });
}

export async function removeTag(tagId: string): Promise<void> {
    await postForm<unknown>('/ca/docs/api/deleteTag.php', { tagid: tagId });
}

function initDocTags(): void {
    const typer = document.getElementById('js-tag-typer') as HTMLInputElement | null;
    const container = document.getElementById('js-tags-container');
    if (!typer || !container) {
        return;
    }

    typer.addEventListener('keypress', async (event) => {
        const key = (event as KeyboardEvent).which || (event as KeyboardEvent).keyCode;
        if (key !== 13 && key !== 44) {
            return;
        }
        event.preventDefault();
        const tag = typer.value.trim();
        const docHidden = document.getElementById('js-hidden-id') as HTMLInputElement | null;
        const docId = docHidden?.getAttribute('docid') || docHidden?.dataset.docid || '';
        if (!tag || !docId) {
            return;
        }
        typer.value = '';
        const res = await addTag(tag, docId);
        if (res?.id) {
            const chip = makeTagChip(res.id, tag);
            typer.insertAdjacentElement('beforebegin', chip);
        }
    });

    container.addEventListener('click', async (e) => {
        const target = e.target as HTMLElement;
        if (!target.classList.contains('js-tag-closer')) {
            return;
        }
        const chip = target.closest('span[data-tid]') as HTMLElement | null;
        if (!chip) {
            return;
        }
        const tid = chip.getAttribute('data-tid') || '';
        await removeTag(tid);
        chip.remove();
    });
}

document.addEventListener('DOMContentLoaded', initDocTags);
